import { API_ROUTES } from "@constants/api-routes";
import { useAccount } from "@context/AccountProvider";
import { useToast } from "@context/ToastContext";
import { apiService, CONTACTS_TIMEOUT } from "@services/axios";
import { useCallback, useEffect, useState } from "react";
import { LeadContactsService } from "../services/leadContactsService";
import {
  CampaignVisibleFieldsConfig,
  LeadContact,
  LeadContactsState,
} from "../types/lead-contacts.types";

export const useLeadContacts = () => {
  const { account } = useAccount();
  const { showError } = useToast();

  const [state, setState] = useState<LeadContactsState>({
    contacts: [],
    groupedContacts: {},
    campaignVisibleFieldsConfig: {},
    loading: true,
    paginationLoading: false,
    error: null,
    page: 1,
    hasMore: true,
    totalPages: 0,
    expandedCampaigns: new Set(),
    campaignPages: {},
    campaignHasMore: {},
    campaignLoading: {},
    selectedLeads: new Set(),
    copiedLeadIds: new Set(),
    showCopiedLeads: false,
    showHistoryModal: false,
    copyHistory: [],
    historyLoading: false,
    historyPage: 1,
    historyTotal: 0,
    historyHasMore: false,
    anchorEl: null,
    menuOpen: false,
    isInitialized: false,
    isMounted: true,
  });

  const fetchContacts = useCallback(
    async (pageNum = 1, isPagination = false) => {
      if (!account?.company_id || !state.isMounted) {
        setState((prev) => ({
          ...prev,
          error: "ID da empresa não encontrado",
          loading: false,
          paginationLoading: false,
        }));
        return;
      }

      try {
        setState((prev) => ({
          ...prev,
          ...(isPagination ? { paginationLoading: true } : { loading: true }),
          error: null,
        }));

        const response = await apiService.getWithTimeout(
          API_ROUTES.CONTACTS.GET_LEADS,
          CONTACTS_TIMEOUT,
          {
            params: {
              companyId: account.company_id,
              page: pageNum,
              limit: 5,
            },
          },
        );

        if (!state.isMounted) return;

        if (response.status === "success" && response.data) {
          const normalGrouped = (response.data as any).groupedByCampaign || {};
          const qualityGrouped =
            (response.data as any).qualityGroupedByCampaign || {};
          const newGroupedContacts = { ...normalGrouped };
          Object.entries(qualityGrouped).forEach(
            ([name, group]: [string, any]) => {
              newGroupedContacts[name] = { ...group, is_quality: true };
            },
          );
          const newHasMore = (response.data as any).hasMore || false;
          const totalPagesFromAPI = (response.data as any).totalPages || 0;
          const totalFromAPI = (response.data as any).total || 0;

          if (
            totalFromAPI === 0 &&
            Object.keys(newGroupedContacts).length === 0
          ) {
            setState((prev) => ({
              ...prev,
              contacts: [],
              groupedContacts: {},
              totalPages: 0,
              hasMore: false,
              page: pageNum,
              campaignHasMore: {},
              campaignPages: {},
            }));
            return;
          }

          // Inicializar estados para campanhas
          const newCampaignHasMore: Record<string, boolean> = {};
          const newCampaignPages: Record<string, number> = {};
          Object.keys(newGroupedContacts).forEach((campaignName) => {
            // hasMore será true se a campanha tiver contatos (total > 0)
            newCampaignHasMore[campaignName] =
              (newGroupedContacts[campaignName]?.total || 0) > 0;
            newCampaignPages[campaignName] = 1;
          });

          setState((prev) => {
            // Preservar contatos já carregados de campanhas expandidas
            const preservedGroupedContacts = { ...prev.groupedContacts };
            Object.keys(newGroupedContacts).forEach((campaignName) => {
              // Se a campanha já tinha contatos carregados, preservar apenas os contatos, mantendo o resumo (total, copied_total, etc) oficial do server
              if (
                preservedGroupedContacts[campaignName]?.contacts?.length > 0
              ) {
                newGroupedContacts[campaignName] = {
                  ...newGroupedContacts[campaignName],
                  contacts: preservedGroupedContacts[campaignName].contacts,
                };
              }
            });

            return {
              ...prev,
              contacts: [], // Array vazio - contatos carregados via fetchContactsByCampaign
              groupedContacts: newGroupedContacts,
              totalPages: totalPagesFromAPI,
              hasMore: newHasMore,
              page: pageNum,
              campaignHasMore: newCampaignHasMore,
              campaignPages: newCampaignPages,
            };
          });
        } else {
          throw new Error("Erro na resposta da API");
        }
      } catch (err: unknown) {
        const errorMessage =
          err && typeof err === "object" && "message" in err
            ? String(err.message)
            : "Erro desconhecido";
        setState((prev) => ({ ...prev, error: errorMessage }));
        showError(errorMessage);
      } finally {
        setState((prev) => ({
          ...prev,
          loading: false,
          paginationLoading: false,
        }));
      }
    },
    [account?.company_id, showError, state.isMounted, state.showCopiedLeads],
  );

  const fetchContactsByCampaign = useCallback(
    async (
      campaignName: string,
      pageNum = 1,
      append = false,
      limit = 10,
      includeCopiedOverride?: boolean,
    ) => {
      if (!account?.company_id || !state.isMounted) {
        return;
      }

      try {
        setState((prev) => ({
          ...prev,
          campaignLoading: { ...prev.campaignLoading, [campaignName]: true },
        }));

        const response = await apiService.getWithTimeout(
          API_ROUTES.CONTACTS.GET_LEADS_BY_CAMPAIGN,
          CONTACTS_TIMEOUT,
          {
            params: {
              companyId: account.company_id,
              campaignName,
              page: pageNum,
              limit: limit,
              includeCopied: includeCopiedOverride ?? state.showCopiedLeads,
            },
          },
        );

        if (!state.isMounted) return;

        if (response.status === "success" && response.data) {
          const newContacts = (response.data as any).contacts || [];
          const hasMoreForCampaign = (response.data as any).hasMore || false;
          const campaignTotal = (response.data as any).campaignTotal || 0;

          setState((prev) => {
            const existingGroup = prev.groupedContacts[campaignName];
            const existingContacts = existingGroup?.contacts || [];
            const existingIds = new Set(
              existingContacts.map((c: LeadContact) => c.id),
            );
            const uniqueNewContacts = newContacts.filter(
              (c: LeadContact) => !existingIds.has(c.id),
            );
            const campaignId =
              existingGroup?.campaign_id ?? newContacts[0]?.campaign_id;

            return {
              ...prev,
              groupedContacts: {
                ...prev.groupedContacts,
                [campaignName]: {
                  contacts: append
                    ? [...existingContacts, ...uniqueNewContacts]
                    : newContacts,
                  total: campaignTotal,
                  copied_total: (response.data as any).copied_total || 0,
                  ...(campaignId != null && { campaign_id: campaignId }),
                },
              },
              campaignPages: { ...prev.campaignPages, [campaignName]: pageNum },
              campaignHasMore: {
                ...prev.campaignHasMore,
                [campaignName]: hasMoreForCampaign,
              },
            };
          });
        }
      } catch (err: unknown) {
        const errorMessage =
          err && typeof err === "object" && "message" in err
            ? String(err.message)
            : "Erro desconhecido";
        showError(errorMessage);
      } finally {
        setState((prev) => ({
          ...prev,
          campaignLoading: { ...prev.campaignLoading, [campaignName]: false },
        }));
      }
    },
    [account?.company_id, showError, state.isMounted, state.showCopiedLeads],
  );

  // Função para carregar todos os contatos de uma campanha
  const loadAllByCampaign = useCallback(
    async (campaignName: string) => {
      if (!account?.company_id || !state.isMounted) {
        return;
      }

      const campaignTotal = state.groupedContacts[campaignName]?.total || 0;
      const currentContacts =
        state.groupedContacts[campaignName]?.contacts || [];

      // Se já carregou todos, não fazer nada
      if (currentContacts.length >= campaignTotal) {
        return;
      }

      try {
        setState((prev) => ({
          ...prev,
          campaignLoading: { ...prev.campaignLoading, [campaignName]: true },
        }));

        // Carregar todos de uma vez usando um limite alto
        // Usar um limite grande para pegar todos de uma vez (máximo 1000 para evitar problemas)
        const response = await apiService.getWithTimeout(
          API_ROUTES.CONTACTS.GET_LEADS_BY_CAMPAIGN,
          CONTACTS_TIMEOUT,
          {
            params: {
              companyId: account.company_id,
              campaignName,
              page: 1,
              limit: Math.min(campaignTotal, 1000),
              includeCopied: state.showCopiedLeads,
            },
          },
        );

        if (!state.isMounted) return;

        if (response.status === "success" && response.data) {
          const newContacts = (response.data as any).contacts || [];
          const campaignTotalFromAPI =
            (response.data as any).campaignTotal || 0;

          setState((prev) => {
            const existingGroup = prev.groupedContacts[campaignName];
            const existingContacts = existingGroup?.contacts || [];
            const existingIds = new Set(
              existingContacts.map((c: LeadContact) => c.id),
            );
            const uniqueNewContacts = newContacts.filter(
              (c: LeadContact) => !existingIds.has(c.id),
            );
            const campaignId =
              existingGroup?.campaign_id ?? newContacts[0]?.campaign_id;

            return {
              ...prev,
              groupedContacts: {
                ...prev.groupedContacts,
                [campaignName]: {
                  contacts: [...existingContacts, ...uniqueNewContacts],
                  total: campaignTotalFromAPI,
                  copied_total: (response.data as any).copied_total || 0,
                  ...(campaignId != null && { campaign_id: campaignId }),
                },
              },
              campaignPages: { ...prev.campaignPages, [campaignName]: 1 },
              campaignHasMore: {
                ...prev.campaignHasMore,
                [campaignName]: false,
              },
            };
          });
        }
      } catch (err: unknown) {
        const errorMessage =
          err && typeof err === "object" && "message" in err
            ? String(err.message)
            : "Erro desconhecido";
        showError(errorMessage);
      } finally {
        setState((prev) => ({
          ...prev,
          campaignLoading: { ...prev.campaignLoading, [campaignName]: false },
        }));
      }
    },
    [
      account?.company_id,
      state.groupedContacts,
      showError,
      state.isMounted,
      state.showCopiedLeads,
    ],
  );

  const loadCopiedIds = useCallback(async () => {
    if (!account?.company_id || !state.isMounted) return;

    try {
      const response = await apiService.get(API_ROUTES.LEAD_COPY.COPIED_IDS);
      if (response && response.status === "success" && response.data) {
        const copiedIds = (response.data as any).copiedLeadIds || [];
        setState((prev) => ({ ...prev, copiedLeadIds: new Set(copiedIds) }));
      }
    } catch (error) {
      console.warn("Erro ao carregar leads copiados:", error);
    }
  }, [account?.company_id, state.isMounted]);

  const loadCampaignVisibleFields = useCallback(async () => {
    if (!account?.company_id || !state.isMounted) return;
    try {
      const configs = await LeadContactsService.getCampaignVisibleFields(
        Number(account.company_id),
      );
      if (!configs) return;
      const map: Record<number, CampaignVisibleFieldsConfig> = {};
      configs.forEach((c) => {
        map[c.campaign_id] = {
          campaign_id: c.campaign_id,
          campaign_name: c.campaign_name,
          visible_fields: Array.isArray(c.visible_fields)
            ? c.visible_fields
            : [],
          field_labels: c.field_labels,
          extra_fields: c.extra_fields ?? [],
          quality_fields: Array.isArray(c.quality_fields)
            ? c.quality_fields
            : undefined,
          field_order: Array.isArray(c.field_order) ? c.field_order : undefined,
        };
      });
      setState((prev) => ({ ...prev, campaignVisibleFieldsConfig: map }));
    } catch (error) {
      console.warn("Erro ao carregar configuração de campos visíveis:", error);
    }
  }, [account?.company_id, state.isMounted]);

  const loadCopyHistory = useCallback(
    async (
      filters?: {
        leadId?: number;
        coordId?: number;
        campaignName?: string;
        origin?: "normal" | "repescagem";
      },
      page: number = 1,
    ) => {
      if (!account?.company_id || !state.isMounted) return;

      setState((prev) => ({ ...prev, historyLoading: true }));
      try {
        const response = await LeadContactsService.getCopyHistory(
          filters,
          page,
          20,
        );
        if (response.history) {
          setState((prev) => ({
            ...prev,
            copyHistory: response.history || [],
            historyPage: page,
            historyTotal: response.total || 0,
            historyHasMore: response.hasMore || false,
          }));
        }
      } catch (error) {
        console.warn("Erro ao carregar histórico de cópias:", error);
        showError("Erro ao carregar histórico de cópias");
      } finally {
        setState((prev) => ({ ...prev, historyLoading: false }));
      }
    },
    [account?.company_id, state.isMounted, showError],
  );

  // Inicialização
  useEffect(() => {
    setState((prev) => ({ ...prev, isMounted: true }));
    const style = document.createElement("style");
    style.textContent = `
            @keyframes fadeInUp {
                from {
                    opacity: 0;
                    transform: translateY(20px);
                }
                to {
                    opacity: 1;
                    transform: translateY(0);
                }
            }
            
            @keyframes fadeIn {
                from {
                    opacity: 0;
                }
                to {
                    opacity: 1;
                }
            }
        `;
    document.head.appendChild(style);

    return () => {
      setState((prev) => ({ ...prev, isMounted: false }));
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  useEffect(() => {
    if (account?.company_id && !state.isInitialized && state.isMounted) {
      setState((prev) => ({ ...prev, isInitialized: true }));
      fetchContacts(1);
    }
  }, [
    account?.company_id,
    state.isInitialized,
    fetchContacts,
    state.isMounted,
  ]);

  useEffect(() => {
    if (state.isInitialized && state.isMounted) {
      loadCopiedIds();
      loadCampaignVisibleFields();
    }
  }, [
    state.isInitialized,
    state.isMounted,
    loadCopiedIds,
    loadCampaignVisibleFields,
  ]);

  return {
    state,
    setState,
    fetchContacts,
    fetchContactsByCampaign,
    loadAllByCampaign,
    loadCopyHistory,
    loadCampaignVisibleFields,
  };
};
